import { useMemo, useState, useCallback } from 'react';
import { addDays, addYears, format } from 'date-fns';
import { DatePicker } from '@app/app/_components/DatePicker/DatePicker';
import { CheckmarkCircleIcon } from '@app/app/_icons/CheckmarkCircleIcon';
import { useTranslations } from 'next-intl';
import { Card } from '@app/app/_components/Card/Card';
import { Heading } from '@app/app/_components/Heading/Heading';

export function InsuranceStartDateStep() {
    const t = useTranslations();
    const today = useMemo(() => new Date(), []);

    const minDate = useMemo(() => addDays(today, 3), [today]);

    const maxDate = useMemo(() => addDays(today, 30), [today]);

    const [selectedBeginDate, setSelectedBeginDate] = useState<Nullable<Date>>(minDate);

    const endDate = useMemo(() => {
        if (!selectedBeginDate) return null;
        return addDays(addYears(selectedBeginDate, 1), -1);
    }, [selectedBeginDate]);

    const displayEndDate = useMemo(() => {
        return endDate ? format(endDate, 'yyyy-MM-dd') : '';
    }, [endDate]);

    const handleBeginDateChange = useCallback((date: Nullable<Date>) => {
        setSelectedBeginDate(date);
    }, []);

    return (
        <Card className="p-6">
            <div className="space-y-4">
                <Heading level="h4">{t('eshop.personalAccidents.insuranceStartDateHeading')}</Heading>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <DatePicker
                        name="beginDate"
                        label={t('eshop.personalAccidents.chooseDate')}
                        showResetButton={false}
                        placeholder={t('eshop.personalAccidents.chooseDate')}
                        defaultValue={minDate}
                        minDate={minDate}
                        maxDate={maxDate}
                        onChange={handleBeginDateChange}
                        alternateFormatting={true}
                    />
                    <input type="hidden" name="endDate" value={displayEndDate} />

                    {selectedBeginDate && endDate && (
                        <div className="inline-flex items-center gap-2 font-medium md:mt-7">
                            <CheckmarkCircleIcon className="size-4 text-primary-400" />
                            <span>
                                <span className="text-neutral-500">
                                    {t('eshop.personalAccidents.insuranceValidTill')}
                                </span>
                                &nbsp;
                                <span>{displayEndDate}</span>
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
}
